import { ArrowLeft, AlertCircle } from 'lucide-react';
import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useVorgang } from '@/adapter/opm-cockpit-backend';
import { ToniTaube } from '@/components/animation/LadeTaubeToni';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { VorgangLockProvider } from '@/features/vorgangLocking/VorgangLockProvider';
import { VorgangBearbeitungContent } from './VorgangBearbeitungContent';

/**
 * Optionaler Navigations-State, mit dem eine Quellseite (z. B. die
 * Massenverarbeitung) ihre Herkunft mitgibt, damit der Rücksprung dorthin
 * zurückführt statt in die Hauptübersicht.
 */
export interface VorgangBearbeitungLocationState {
  from?: string;
}

export const VorgangBearbeitungPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // React Query hooks
  const { data, isLoading, error: queryError, refetch } = useVorgang(id);
  const from = (location.state as VorgangBearbeitungLocationState | null)?.from;
  const backPath = from ?? '/vorgaenge';

  const [error, setError] = useState<string | null>(null);

  const onLockStatusChange = useCallback(
    (isLockedByOther: boolean, lockedBy?: string) => {
      if (isLockedByOther && lockedBy) {
        setError(
          `Vorgang wurde von ${lockedBy} übernommen. Sie können keine Änderungen mehr vornehmen.`,
        );
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(backPath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
        </div>
        <div className="flex items-center justify-center py-8">
          <ToniTaube width={300} height={300} />
        </div>
      </div>
    );
  }

  if ((queryError || error) && !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(backPath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </Button>
          <h1 className="text-2xl font-bold text-text-primary">Fehler</h1>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fehler</AlertTitle>
          <AlertDescription>{error || queryError?.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data) return null;

  return (
    <VorgangLockProvider
      vorgang={data.vorgang}
      refetch={refetch}
      onLockStatusChange={onLockStatusChange}
      autoLock={!data.isArchived}
    >
      <VorgangBearbeitungContent
        vorgang={data.vorgang}
        protokoll={data.vorgangProtokoll}
        isArchived={data.isArchived ?? false}
        backPath={backPath}
        error={error}
        setError={setError}
        navigate={navigate}
      />
    </VorgangLockProvider>
  );
};
